/**
 * Toggles the 'active' class modifier on favorite buttons when clicking
 * @module toggleFavorite
 */

import $ from 'jquery';
import modal from '../modules/modal';
import notificationPopup from '../modules/notification-popup';
import registerJQueryPlugin from '../lib/register-jquery-plugin';

export const PLUGIN_NAME = 'toggleFavorite';
export const ACTIVE_CLASS = 'favorite-button--active';
export const VISUAL_HIDDEN_CLASS = 'u-screen-reader';
export const MODAL_CLASS = 'modal';
export const EVENT_NAME = 'favorite';

/**
 * Defines sensible defaults for toggle favorite.
 * @type {Object}
 */
const TOGGLE_FAVORITE_OPTIONS = {
  modal,
  notificationPopup,
  container: document.body,
};

function getActiveClass() {
  return (window.rcProps && window.rcProps.favoriteActiveClass) || ACTIVE_CLASS;
}

/**
 * Central place to see if a specific element has been favorited.
 * @private
 * @param {jQuery} $el - Container for the favorite button
 * @returns {boolean} Whether the item has been favorited
 */
const isFavorited = $el => $el.hasClass(getActiveClass());

/**
 * Shows the appropriate favorite related message for screen readers.
 * @private
 * @param {jQuery} $el - Container for the favorite button
 * @param {boolean} hasFavorite - Whether it has been favorited
 */
const showMessage = ($el, hasFavorite) => {
  let message = $el.data('favoriteMessage');
  if (hasFavorite) {
    message = $el.data('unfavoriteMessage');
  }

  $el.find(`.${VISUAL_HIDDEN_CLASS}`).text(message);
};

/**
 * Responsible for showing the notification based on whether the item has been
 * favorited.
 * @private
 * @param {jQuery} $el - Container for the favorite button
 * @param {boolean} hasFavorite - Whether it has been favorited
 * @param {Object} options - Configuration options
 */
const showNotification = ($el, hasFavorite, { notificationPopup: notificationFunc, container }) => {
  let message = $el.data('unfavoriteNotification');
  if (hasFavorite) {
    message = $el.data('favoriteNotification');
  }

  notificationFunc({
    message,
    container,
  });
};

/**
 * Responsible for initializing any visual hidden text related to the state of
 * the favorite button.
 * @private
 * @param {jQuery} $el - Container for the favorite button
 */
const initializeVisualHidden = ($el) => {
  const hasFavorite = isFavorited($el);
  const el = document.createElement('span');
  el.className = VISUAL_HIDDEN_CLASS;
  $el.prepend(el);
  showMessage($el, hasFavorite);
};

/**
 * Responsible for dispatching a favorited event.
 * @private
 * @param {HTMLElement} el - Element triggering event
 * @param {boolean} favorited - Whether the element was favorited
 */
const dispatchFavoriteEvent = (el, favorited, callback) => {
  const evt = new CustomEvent(EVENT_NAME, {
    detail: { favorited, callback },
  });

  el.dispatchEvent(evt);
};

/**
 * Toggle the favorited state
 * @private
 * @param {jQuery} $el - Element to toggle
 * @param {Object} opts - Configurable options
 */
const toggleActive = ($el, opts) => {
  const code = $el.data('code');
  const productSize = $('#tr_top_heart_mhp_count').data('count');
  const activeClass = getActiveClass();
  const hasFavorite = !isFavorited($el);

  if (code) {
    if (window.rcProps.isGuest &&
        !(window.rcProps.hearts.enabledforGuest &&
            productSize <= window.rcProps.hearts.maxCountGuest)) {
      const refUrl = encodeURIComponent(window.location.pathname + window.location.search);
      $.modal({
        url: '/content/favorites/fav-max-popup-markup',
        triggerOpen: true,
        onOpen: () => {
          $('#fav-create-link').attr('href', `/r/mobile/LoginPage.jsp?favcode=${code}&ref=${refUrl}`);
        },
      });
      return;
    } else if (!window.rcProps.isGuest && productSize > 1999) {
      $.modal({
        url: '/content/favorites/fav-max-popup-markup',
        triggerOpen: true,
      });
      return;
    }

    dispatchFavoriteEvent($el[0], hasFavorite, () => {
      $el.toggleClass(activeClass);
      if ($el.attr('id') === 'product-zoom-favorite') {
        $('.js-fav-button').toggleClass(activeClass);
      }
      showMessage($el, hasFavorite);
      showNotification($el, hasFavorite, opts);
    });
  } else {
    $el.toggleClass(activeClass);
    showMessage($el, hasFavorite);
    showNotification($el, hasFavorite, opts);
    dispatchFavoriteEvent($el[0], hasFavorite);
  }
  if (opts && typeof opts.logFavoriteClick === 'function') {
    opts.logFavoriteClick(hasFavorite);
  }
};

/**
 * Sets up click event toggle for favorite heart button.
 * @private
 * @see TOGGLE_FAVORITE_OPTIONS
 * @param {HTMLElement} el - Elements matching the selector (likely .favorite-button)
 * @param {Object} options - Configurable options
 */
export default function toggleFavorite(el, options = {}) {
  const $el = $(el);
  const opts = Object.assign({}, TOGGLE_FAVORITE_OPTIONS, options);
  initializeVisualHidden($el);

  $el.on('click', () => toggleActive($el, opts));
}

// Expose the function as a jQuery plugin for ease of use
registerJQueryPlugin(PLUGIN_NAME, toggleFavorite);



// WEBPACK FOOTER //
// ./styleguide/src/assets/elements/toggle-favorite.js